// Base API configuration and utilities

export interface ApiResponse<T = any> {
	data: T;
	success: boolean;
	message?: string;
	errors?: string[];
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
	pagination: {
		currentPage: number;
		totalPages: number;
		totalItems: number;
		itemsPerPage: number;
		hasNext: boolean;
		hasPrev: boolean;
	};
}

export interface ApiError {
	message: string;
	status: number;
	code?: string;
	details?: any;
}

// Base API configuration
export const API_CONFIG = {
	// Update this with your actual API base URL
	baseURL: import.meta.env.VITE_BASE_URL || '/api',

	// Default headers
	headers: {
		'Content-Type': 'application/json',
	},

	// Timeout in milliseconds
	timeout: 30000,

	// Retry configuration
	retryAttempts: 3,
	retryDelay: 1000,
} as const;

// Utility function to build API URLs
export const buildUrl = (endpoint: string, params?: Record<string, string | number>): string => {
	const url = new URL(endpoint, API_CONFIG.baseURL);

	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				url.searchParams.append(key, String(value));
			}
		});
	}

	return url.toString();
};

// Utility function to handle API responses
export const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
	try {
		// Check content type to determine how to parse response
		const contentType = response.headers.get('content-type');
		let data: any;

		if (contentType && contentType.includes('application/json')) {
			data = await response.json();
		} else {
			// Handle text responses (like delete messages)
			const textData = await response.text();
			data = textData;
		}

		if (!response.ok) {
			const errorMessage = typeof data === 'object' ? data.message : data;
			const error: any = new Error(errorMessage || `HTTP ${response.status}: ${response.statusText}`);
			// Attach response data and status to error for better error handling
			error.response = {
				data: data,
				status: response.status,
				statusText: response.statusText,
			};
			throw error;
		}

		// Check if the response is already in ApiResponse format
		if (data && typeof data === 'object' && 'data' in data && 'success' in data) {
			// Response is already in ApiResponse format
			return data;
		} else {
			// Response is raw data, wrap it in ApiResponse format
			return {
				data: data as T,
				success: true,
				message: typeof data === 'string' ? data : 'Success',
			};
		}
	} catch (error) {
		if (error instanceof SyntaxError) {
			throw new Error('Invalid response format from server');
		}
		throw error;
	}
};

// Utility function to handle API errors consistently
export const handleApiError = (error: unknown): ApiError => {
	if (error instanceof Error) {
		return {
			message: error.message,
			status: 500, // Default status for client-side errors
			code: 'CLIENT_ERROR',
		};
	}

	if (typeof error === 'object' && error !== null) {
		return {
			message: (error as any).message || 'An unknown error occurred',
			status: (error as any).status || 500,
			code: (error as any).code,
			details: error,
		};
	}

	return {
		message: 'An unknown error occurred',
		status: 500,
		code: 'UNKNOWN_ERROR',
	};
};

// Base API client with common functionality
export class ApiClient {
	private baseURL: string;
	private defaultHeaders: Record<string, string>;

	constructor(baseURL = API_CONFIG.baseURL, defaultHeaders = API_CONFIG.headers) {
		this.baseURL = baseURL;
		this.defaultHeaders = defaultHeaders;
	}

	private buildInstanceUrl(endpoint: string, params?: Record<string, string | number>): string {
		const base = this.baseURL.endsWith('/') ? this.baseURL : `${this.baseURL}/`;
		const path = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
		const url = new URL(path, base);
		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					url.searchParams.append(key, String(value));
				}
			});
		}
		return url.toString();
	}

	private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
		const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;

		// Get authorization header from token storage
		let authHeader = {};
		if (typeof window !== 'undefined') {
			// Dynamic import to avoid SSR issues
			const { getAuthHeader } = await import('./tokenStorage');
			const authorization = getAuthHeader();
			if (authorization) {
				authHeader = { Authorization: authorization };
				console.log('ApiClient: Using authorization header for', endpoint);
			} else {
				console.log('ApiClient: No authorization token available for', endpoint);
			}
		}

		const config: RequestInit = {
			...options,
			headers: {
				...this.defaultHeaders,
				...authHeader,
				...options.headers,
			},
		};

		if (config.body instanceof FormData && config.headers) {
			delete (config.headers as Record<string, string>)['Content-Type'];
		}

		try {
			const response = await fetch(url, config);

			// Handle 401 Unauthorized - token expired or invalid
			if (response.status === 401) {
				console.warn('ApiClient: 401 Unauthorized detected - Token expired or invalid');
				console.log('ApiClient: Request details:', { endpoint, method: config.method });

				// Dispatch custom event for auto-logout
				if (typeof window !== 'undefined') {
					const event = new CustomEvent('auth:token-expired', {
						detail: {
							endpoint,
							status: 401,
							timestamp: new Date().toISOString(),
						},
					});
					window.dispatchEvent(event);
					console.log(
						'ApiClient: ✓ Dispatched auth:token-expired event - Auto-logout will be triggered',
					);
				}
			}

			return await handleResponse<T>(response);
		} catch (error) {
			// throw handleApiError(error);
			throw error;
		}
	}

	async get<T>(
		endpoint: string,
		params?: Record<string, string | number>,
	): Promise<ApiResponse<T>> {
		const url = this.buildInstanceUrl(endpoint, params);
		return this.request<T>(url, { method: 'GET' });
	}

	async post<T>(
		endpoint: string,
		data?: any,
		params?: Record<string, string | number>,
	): Promise<ApiResponse<T>> {
		const url = this.buildInstanceUrl(endpoint, params);
		const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

		return this.request<T>(url, {
			method: 'POST',
			body: isFormData ? data : data ? JSON.stringify(data) : undefined,
			headers: isFormData ? {} : undefined,
		});
	}

	async put<T>(
		endpoint: string,
		data?: any,
		params?: Record<string, string | number>,
	): Promise<ApiResponse<T>> {
		const url = this.buildInstanceUrl(endpoint, params);
		return this.request<T>(url, {
			method: 'PUT',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	async patch<T>(
		endpoint: string,
		data?: any,
		params?: Record<string, string | number>,
	): Promise<ApiResponse<T>> {
		const url = this.buildInstanceUrl(endpoint, params);
		return this.request<T>(url, {
			method: 'PATCH',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	async delete<T>(
		endpoint: string,
		data?: any,
		params?: Record<string, string | number>,
	): Promise<ApiResponse<T>> {
		const url = this.buildInstanceUrl(endpoint, params);
		return this.request<T>(url, {
			method: 'DELETE',
			body: data ? JSON.stringify(data) : undefined,
		});
	}
}

// Default API client instance
export const apiClient = new ApiClient();
export const apiClientChat = new ApiClient(API_CONFIG.headers);
